import type { ConnectError } from '@connectrpc/connect'
import {
  PlanSubmissionErrorReason,
  PlanSubmissionErrorReasonSchema,
} from '@ebalance/proto/bffuser/v1/procurement_plan_model_pb'
import {
  WorkflowCaseErrorReason,
  WorkflowCaseErrorReasonSchema,
} from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import type { TFunction } from 'i18next'

import {
  type AppErrorInfoReasonEntry,
  resolveAppErrorInfoMessage,
} from '~/libs/appErrorInfo'

/**
 * `WorkflowCaseErrorReason` (backend `workflowcase` domain) → i18n key
 * registry for the サブステップ完了確認ダイアログ (`SubStepCompleteDialog`,
 * AC-WFW-007, issue #5341, workflow-approver-all-inactive-block Design
 * Note).
 *
 * `APPROVER_ALL_INACTIVE` is operation-scoped (`field` なし = ダイアログの
 * custom 誘導表示、承認フロー設定画面へのリンク付き — Design Note UI
 * Interactions)。単一入力欄に帰属しない (承認段階そのものが未設定 / 承認者
 * が全員無効という前提の欠落であり、フォーム項目の誤りではない)。
 *
 * `WORKFLOW_CASE_DELETED` はこのダイアログの操作 (完了にする) の error 経路では
 * 発生しない reason (発生元は plan-submit の連動完了トリガー) なので、専用文言を
 * 持たせず既存の `completeFailed` code ベース fallback に委ねる。
 */
const COMPLETE_DIALOG_REASON_KEYS: Record<
  WorkflowCaseErrorReason,
  AppErrorInfoReasonEntry | undefined
> = {
  [WorkflowCaseErrorReason.UNSPECIFIED]: undefined,
  [WorkflowCaseErrorReason.APPROVER_ALL_INACTIVE]: {
    key: 'page:workflowCaseDetail.subStep.completeDialog.approverAllInactive.message',
  },
  [WorkflowCaseErrorReason.WORKFLOW_CASE_DELETED]: undefined,
  // issue #5219 Decisions #13/#15: TEMPLATE_REQUIRES_BG is emitted only by
  // CreateWorkflowCases (workflow-case creation), which this dialog's
  // operation (完了にする) never calls — no dedicated message, falls back
  // to the existing code-based message.
  [WorkflowCaseErrorReason.TEMPLATE_REQUIRES_BG]: undefined,
  // MANUAL_COMPLETION_AWAITING_APPROVAL is emitted only by the plan submit
  // (計画画面からの提出); this dialog never triggers it.
  [WorkflowCaseErrorReason.MANUAL_COMPLETION_AWAITING_APPROVAL]: undefined,
  // SUSPENDED (AC-WFW-043): 中断の画面と文言は未実装。
  // TODO(#6680): 画面と文言を入れる。それまでは code ベースの fallback。
  [WorkflowCaseErrorReason.SUSPENDED]: undefined,
}

/**
 * "完了にする" 確定 (`SubStepCompleteDialog`) の失敗 error から、AC-WFW-007
 * (承認者全員無効) の誘導メッセージを解決する。`google.rpc.ErrorInfo` detail
 * が無い / reason が未知 / registry entry が `undefined` の場合は `undefined`
 * を返し、呼び出し元の code ベース fallback (`completeFailed` /
 * `aborted`) に委ねる。
 */
export function resolveApproverAllInactiveMessage(
  err: ConnectError,
  t: TFunction,
): string | undefined {
  return resolveAppErrorInfoMessage(
    err,
    WorkflowCaseErrorReasonSchema,
    COMPLETE_DIALOG_REASON_KEYS,
    t,
  )?.message
}

/**
 * 承認ステージの承認 (`ApproveSubStepStage`) が取引先の行の欠けで拒否された時の
 * 文言 (AC-PLC-083)。 reason は計画提出の拒否と同じ `PlanSubmissionErrorReason`。
 * 該当しない error は undefined を返し、 呼び出し元の code 別の文言に委ねる。
 */
const APPROVAL_BLOCKED_REASON_KEYS: Record<
  PlanSubmissionErrorReason,
  AppErrorInfoReasonEntry | undefined
> = {
  [PlanSubmissionErrorReason.UNSPECIFIED]: undefined,
  [PlanSubmissionErrorReason.OCCTO_CREDENTIAL_NOT_CONFIGURED]: undefined,
  [PlanSubmissionErrorReason.SUPERSEDED_VERSION]: undefined,
  [PlanSubmissionErrorReason.MISSING_BASELINE_BOXES]: {
    key: 'page:workflowCaseDetail.error.approvalBlockedByMissingBaselineBoxes',
  },
  // AC-PLC-085 は担当者が計画画面から直接出す提出だけを拒否する。 承認後の送信は
  // 時間帯で止めない (AC-PLC-062) ので、 承認の操作には返らない。
  [PlanSubmissionErrorReason.DIRECT_SUBMIT_HELD]: undefined,
}

export function resolveApprovalBlockedMessage(
  err: ConnectError,
  t: TFunction,
): string | undefined {
  return resolveAppErrorInfoMessage(
    err,
    PlanSubmissionErrorReasonSchema,
    APPROVAL_BLOCKED_REASON_KEYS,
    t,
  )?.message
}
